import { Formik, Form, FieldArray, Field } from 'formik';
import { Modal, FormInput, Button, Label } from '@component';
import Swal from 'sweetalert2/dist/sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import { useServices } from '@services';
import { toast } from 'react-toastify';

export const QuestionForm = ({ quizId, question, onClose, saved }) => {
  const { db } = useServices();

  const parseQuestion = q => {
    if (!q || Object.keys(q).length === 0) return { text: '', options: ['', ''], correctIndex: 0 };
    const opts = q.optionsOrder?.map(id => q.options?.[id]?.text || '') || [];
    while (opts.length < 2) opts.push('');
    const correctIndex = q.optionsOrder?.indexOf(q.correctOptionId) ?? 0;
    return { id: q.id, text: q.text, options: opts, correctIndex };
  };

  const initial = parseQuestion(question);

  const validate = values => {
    const errors = {};
    if (!values.text) errors.text = 'Required';
    if (values.options) {
      values.options.forEach((o, i) => {
        if (!o) {
          if (!errors.options) errors.options = [];
          errors.options[i] = 'Required';
        }
      });
    }
    return errors;
  };

  const submit = (values, { setSubmitting }) => {
    const ids = values.options.map((_, i) => `o${i + 1}`);
    const options = {};
    ids.forEach((id, i) => {
      options[id] = { text: values.options[i] };
    });
    const payload = {
      text: values.text,
      options,
      optionsOrder: ids,
      correctOptionId: ids[values.correctIndex],
      modifiedOn: Math.floor(Date.now() / 1000)
    };
    const ref = db.collection('quizData').doc(quizId).collection('questions');
    const docRef = values.id ? ref.doc(values.id) : ref.doc();
    if (!values.id) payload.id = docRef.id;
    docRef
      .set(payload)
      .then(() => db.updateQuestionCount(quizId))
      .then(() => db.updateQuizModifiedOn())
      .then(() => {
        toast.success('Question saved');
        saved();
        onClose();
      })
      .catch(() => toast.error('Failed to save question'))
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal
      visible={!!question}
      onClose={onClose}
      title={question ? 'Edit Question' : 'Add Question'}
      className="text-black w-full max-w-3xl"
    >
      <Formik initialValues={initial} validate={validate} onSubmit={submit} enableReinitialize>
        {({ values, isSubmitting, setFieldValue }) => (
          <Form>
            <Label htmlFor="text">Question *</Label>
            <FormInput name="text" />
            <FieldArray name="options">
              {({ push, remove }) => (
                <>
                  {values.options.map((o, i) => (
                    <div key={i} className="mt-2">
                      <Label htmlFor={`option_${i}`}>Option {i + 1} *</Label>
                      <FormInput name={`options.${i}`} />
                      <Field
                        type="radio"
                        name="correctIndex"
                        value={String(i)}
                        checked={values.correctIndex === i}
                        onChange={() => setFieldValue('correctIndex', i)}
                        className="mr-2"
                        id={`correct-${i}`}
                      />
                      Correct
                      {values.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            Swal.fire({
                              title: 'Are you sure?',
                              text: 'Delete this option?',
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonText: 'Yes, delete it!',
                              cancelButtonText: 'No, keep it'
                            }).then(result => {
                              if (result.value) remove(i);
                            });
                          }}
                          className="text-primary-500 ml-2"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => push('')} className="text-primary-500 mt-2">
                    Add Option
                  </button>
                </>
              )}
            </FieldArray>
            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting} className="mt-4">
                Save
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};